import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";

/**
 * A search box over a list that is already in the browser.
 *
 * ⚠️ **THE DISTINCTION THAT MAKES PER-KEYSTROKE SAFE HERE.** The queue's subject search runs
 * on SUBMIT and never while typing, because every row it returns files a PHI read against the
 * searcher's name (D-78, D-33) — so a live search files the matches of `p`, `pr` and `pri` on
 * the way to `printer`, against requests nobody asked to see. This searches a list the server
 * has already handed over: it fetches nothing, reads nothing and audits nothing, so there is no
 * event to file and no reason to make somebody press Enter. `SearchableOptions` says the same
 * thing about the filter pickers, for the same reason.
 *
 * ⚠️ **It filters, it does not fetch.** Nothing here narrows a query — the rows were all going
 * to be drawn anyway — so a search box on a settings list can neither widen nor narrow what
 * anybody is permitted to see. That is why it needs no gate and no cap.
 *
 * ⚠️ **It says how much it is hiding.** A list that has been silently shortened is the failure
 * the queue's filter chips exist to prevent, and it is the same failure on a settings screen:
 * somebody scans a list of ten roles, does not see the one they made, and concludes it was not
 * saved. The count is the chip.
 *
 * ⚠️ **A threshold, because a search box over six things is furniture.** It costs a row of
 * vertical space and answers a question nobody had.
 */
export function ListSearch({
  value,
  onChange,
  label,
  placeholder,
  total,
  showing,
  threshold = 8,
}: {
  value: string;
  onChange: (next: string) => void;

  /** What is being searched, for the accessible name — "roles", "saved replies". */
  label: string;

  placeholder?: string;

  /** How many there are in total. Below `threshold` the box is not drawn at all. */
  total: number;

  /** How many the term matches, so the box can say what it is hiding. */
  showing: number;

  threshold?: number;
}) {
  if (total < threshold) return null;

  const term = value.trim();

  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-2.5 py-1.5">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />

        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? `Search ${label}…`}
          aria-label={`Search ${label}`}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />

        {term !== "" && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear the search"
            className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>

      {/* ⚠️ Drawn only while a term is applied, and it names the whole as well as the part: a
          list showing 3 of 24 is a filtered list, and "3" alone is a claim about how many there
          are. Same reasoning as the reporting screens keeping the denominator on screen (D-53). */}
      {term !== "" && (
        <p className="mt-1 text-xs text-muted-foreground" role="status">
          {showing === 0
            ? `Nothing matches “${term}”. ${total} in total.`
            : `Showing ${showing} of ${total}.`}
        </p>
      )}
    </div>
  );
}

/**
 * Narrows a list on a term, matched against whatever text each row makes searchable.
 *
 * ⚠️ Case-insensitive substring over the fields the caller names, and nothing cleverer. A
 * fuzzy match on an administration screen means somebody types the exact name of a role and is
 * shown three others above it, which is worse than no search — these lists are short enough
 * that an exact substring always finds what somebody meant.
 *
 * ⚠️ A `null` or `undefined` field is skipped rather than stringified: `String(null)` is
 * `"null"`, which matches a search for "null" and, more usefully, for "ul".
 */
export function useListSearch<T>(
  items: readonly T[] | null,
  term: string,
  text: (item: T) => (string | null | undefined)[],
): T[] {
  return useMemo(() => {
    if (items === null) return [];

    const needle = term.trim().toLowerCase();
    if (needle === "") return [...items];

    return items.filter((item) =>
      text(item).some((field) => field != null && field.toLowerCase().includes(needle)),
    );
    // `text` is a fresh closure on every render at most call sites; depending on it would make
    // this memo useless. The fields it reads come from `items`, which is depended on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, term]);
}
